import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { StorefrontHeader } from '@/components/layout/StorefrontHeader'
import { Footer } from '@/components/layout/Footer'
import { ToastContainer } from '@/components/common/Toast'
import { OnboardingLauncher } from '@/components/onboarding/OnboardingLauncher'
import { AuthModals } from '@/components/auth/AuthModals'
import { MessageModal } from '@/components/messages/MessageModal'
import { useAuthInit } from '@/hooks/useAuth'
import { useCartSubscription } from '@/hooks/useCart'
import { useNotificationSubscription } from '@/hooks/useNotifications'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore, isAdmin } from '@/store/useAuthStore'
import { useFeatureFlagStore } from '@/store/useFeatureFlagStore'
import { CartDrawer } from '@/components/cart/CartDrawer'

// Routes that an authed-but-unverified user is still allowed to visit. Every
// other path is force-redirected to /verify-email until they click the
// verification link in their inbox.
const VERIFICATION_EXEMPT_PATHS = new Set(['/verify-email', '/auth/action'])

export function App() {
  // Initialize auth listener on app mount
  useAuthInit()

  // Subscribe to cart (real-time Firestore listener)
  useCartSubscription()

  // Subscribe to notifications (real-time Firestore listener)
  useNotificationSubscription()

  // Load admin-managed feature flags into the category module cache app-wide
  useFeatureFlags()

  // Scroll to top on route changes
  const { pathname } = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  // Global email-verification gate. Signed-in users without a verified email
  // are bounced to /verify-email on every route change. Admins/demo are
  // exempt so internal tooling keeps working. Admin-toggleable via the
  // emailVerificationRequired feature flag — we wait for flags to load so
  // nobody is bounced on the cautious default while the real value is in
  // flight.
  const authUser = useAuthStore((s) => s.user)
  const authProfile = useAuthStore((s) => s.profile)
  const authLoading = useAuthStore((s) => s.isLoading)
  const featureFlags = useFeatureFlagStore((s) => s.flags)
  const flagsLoaded = useFeatureFlagStore((s) => s.loaded)
  useEffect(() => {
    if (authLoading) return
    if (!authUser) return
    if (authUser.isAnonymous) return // guest sessions never verify email
    if (authUser.emailVerified) return
    if (isAdmin(authUser.email, authProfile)) return
    if (!flagsLoaded || !featureFlags.emailVerificationRequired) return
    if (VERIFICATION_EXEMPT_PATHS.has(pathname)) return
    navigate('/verify-email', { replace: true })
  }, [authLoading, authUser, authProfile, flagsLoaded, featureFlags.emailVerificationRequired, pathname, navigate])

  const { activeModal, modalData, closeModal } = useUIStore()
  const isMessagesRoute = pathname.startsWith('/messages')

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Skip to content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary-500 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-medium focus:outline-none"
      >
        Skip to main content
      </a>
      <StorefrontHeader />
      <main id="main-content" className={isMessagesRoute ? 'flex-1 overflow-hidden' : 'flex-1'}>
        <Outlet />
      </main>
      {!isMessagesRoute && <Footer />}
      <AuthModals />
      <MessageModal
        isOpen={activeModal === 'message'}
        onClose={closeModal}
        data={activeModal === 'message' ? modalData as {
          listingId: string
          sellerId: string
          sellerName: string
          listingTitle: string
          listingImage?: string
          listingPrice?: number
        } : undefined}
      />
      <CartDrawer />
      <OnboardingLauncher />
      <ToastContainer />
    </div>
  )
}
